import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import ReactPortal from 'components/ReactPortal';
import Tooltip from 'components/Tooltip';
import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';
import { InsightsChatContext } from 'contextProviders/InsightsChatProvider';
import { InsightsContentContext } from 'contextProviders/InsightsContentProvider';

import { INSIGHTS_DOCK_ELEMENT_ID } from '../../constants';
import InsightsChatConversation from './InsightsChatConversation';
import InsightsConversationList from './InsightsConversationList';
import DownloadChatLogButton from './downloadChatLog/DownloadChatLogButton';
import InsightsMemoryModal from './memory/InsightsMemoryModal';
import { BUILD_WITH_AI_LABEL } from './OpenInsightsButton';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 16px 20px;
  background-color: var(--color-bg-primary);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UsageLabel = styled.div`
  color: var(--color-text-secondary);
  white-space: nowrap;
  padding: 3px 10px;
  border-radius: 999px;
  background-color: var(--color-bg-secondary);
  border: 1px solid var(--color-border-secondary);
`;

const formatUsd = (value: number): string => `$${value.toFixed(2)}`;

const InsightsChat = () => {
  const {
    close,
    messages,
    usageSpentUsd,
    budgetUsd,
    startNewChat,
    conversations,
    refreshConversations,
  } = useContext(InsightsChatContext);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  // Over Studio, where the agent builds the canvas
  const { getStudioBuilder } = useContext(InsightsContentContext);
  const title = getStudioBuilder() ? BUILD_WITH_AI_LABEL : 'Insights';
  const [showMemory, setShowMemory] = useState<boolean>(false);

  // Docked alongside the content (see InsightsChatDock), not overlaid: the panel
  // portals into the dock column, which owns its width and open/closed clipping —
  // so the report behind stays visible and interactive. No backdrop, and the
  // report's own Highcharts tooltips are left alone (they no longer overlap the
  // chat, which now lives in its own column).
  return (
    <ReactPortal elementId={INSIGHTS_DOCK_ELEMENT_ID}>
      <Container data-testid="insights-chat-panel">
        <Header>
          <TitleRow>
            <Icon
              icon="sparkles"
              width={20}
              height={20}
              color="var(--color-ai-accent)"
            />
            <Typography.Header type="H4">{title}</Typography.Header>
          </TitleRow>
          <HeaderRight>
            {budgetUsd !== null && (
              <UsageLabel data-testid="insights-usage">
                <Typography.Body type="Body 12">
                  {usageSpentUsd != null ? formatUsd(usageSpentUsd) : '—'} /{' '}
                  {formatUsd(budgetUsd)} this month
                </Typography.Body>
              </UsageLabel>
            )}
            {(showHistory || conversations.length > 0) && (
              <Tooltip content={showHistory ? 'Back' : 'History'}>
                <Button
                  type="Ghost"
                  size="Small"
                  icon={showHistory ? 'arrow-left' : 'clock'}
                  onClick={() => {
                    if (showHistory) {
                      setShowHistory(false);
                    } else {
                      refreshConversations();
                      setShowHistory(true);
                    }
                  }}
                  testId="insights-history-toggle"
                />
              </Tooltip>
            )}
            <DownloadChatLogButton />
            {messages.length > 0 && (
              <Tooltip content="New chat">
                <Button
                  type="Ghost"
                  size="Small"
                  icon="note-stack-add"
                  onClick={() => {
                    setShowHistory(false);
                    startNewChat();
                  }}
                  testId="insights-new-chat"
                />
              </Tooltip>
            )}
            <Tooltip content="Memory">
              <Button
                type="Ghost"
                size="Small"
                icon="database"
                onClick={() => setShowMemory(true)}
                testId="insights-memory-open"
              />
            </Tooltip>
            <Tooltip content="Close">
              <Button
                type="Ghost"
                size="Small"
                icon="cross"
                onClick={close}
                testId="insights-close"
              />
            </Tooltip>
          </HeaderRight>
        </Header>

        {showMemory && (
          <InsightsMemoryModal onClose={() => setShowMemory(false)} />
        )}

        {showHistory ? (
          <InsightsConversationList onSelect={() => setShowHistory(false)} />
        ) : (
          <InsightsChatConversation />
        )}
      </Container>
    </ReactPortal>
  );
};

export default InsightsChat;
